// FP 간트(자체 구현) — 캔버스 렌더 프리미티브
//
// 좌표 계산은 하지 않는다. 호출부(GanttFrame + 화면)가 "이 사각형에 이걸 그려라"까지 정해서 넘긴다
// — 그래야 같은 프리미티브를 자원 간트(행당 수백 박스)와 주문 간트(행당 1박스)가 같이 쓴다.
//
// 그림 규칙(색·크기·마크 위치)은 old TreeGrid CSS에서 가져온 값을 `ganttConstants.js`에 모아두고
// 여기서는 그 값으로 그리기만 한다. 근거는 reports/gantt-implementation.md §5-2a F5/F8.

import {
  BAR_BORDER_COLOR,
  BAR_DEFAULT_COLOR,
  BAR_FONT,
  BAR_HOVER_SHADE,
  BAR_EDGE_LIGHTEN,
  BAR_EDGE_STEPS,
  BAR_LABEL_MIN_HEIGHT,
  BAR_RADIUS,
  CHANGED_MARK_COLOR,
  CHANGED_MARK_SIZE,
  DRAG_TIP_BG,
  DRAG_TIP_COLOR,
  DRAG_TIP_FONT,
  DRAG_TIP_OFFSET_Y,
  DRAG_TIP_PAD_X,
  DRAG_TIP_PAD_Y,
  DROP_BAND_RADIUS,
  DROP_CARET_SIZE,
  DROP_PLACEHOLDER_DASH,
  DROP_DENY_EDGE,
  DROP_DENY_FILL,
  DROP_OK_EDGE,
  DROP_OK_FILL,
  DROP_PUSH_EDGE,
  DROP_PUSH_FILL,
  ORIGIN_OUTLINE_COLOR,
  ORIGIN_OUTLINE_DASH,
  PUSHED_RAIL_COLOR,
  PUSHED_RAIL_WIDTH,
  DEP_ARROW_SIZE,
  DEP_CORNER_RADIUS,
  DEP_CURVE_K_MAX,
  DEP_CURVE_K_MIN,
  DEP_ELBOW_MARGIN,
  DEP_ENTRY_STUB,
  DEP_FOCUS_HALO_WIDTH,
  DEP_FOCUS_LINE_COLOR,
  DEP_FOCUS_LINE_WIDTH,
  DEP_HALO_COLOR,
  DEP_HALO_WIDTH,
  DEP_LINE_COLOR,
  DEP_LINE_STYLE,
  DEP_LINE_WIDTH,
  DEP_WRAP_GAP_THRESHOLD,
  DIVIDE_MARK_FILL,
  DIVIDE_MARK_HEIGHT_RATIO,
  DIVIDE_MARK_JC_HEIGHT_RATIO,
  DIVIDE_MARK_MIN_RAIL,
  DOWNTIME_BG_COLOR,
  DOWNTIME_EDGE_COLOR,
  DOWNTIME_HATCH_COLOR,
  DOWNTIME_MARK_BORDER_COLOR,
  FROZEN_ZONE_BORDER_COLOR,
  FROZEN_ZONE_COLOR,
  JC_BAR_HEIGHT_RATIO,
  JC_BAR_MIN_HEIGHT,
  JC_LINK_CAP_HEIGHT,
  JC_LINK_COLOR,
  JC_LINK_DASH,
  JC_LINK_MIN_WIDTH,
  JC_NEXT_COLOR,
  JC_PREV_COLOR,
  LANE_BAR_MIN_WIDTH,
  LANE_BAR_TEXT_MIN_WIDTH,
  LANE_BAR_TEXT_NARROW_PAD,
  LANE_BAR_TEXT_NARROW_WIDTH,
  LANE_DIM_ALPHA,
  LANE_ROW_DIVIDER_COLOR,
  LANE_SELECTION_CASING,
  LANE_SELECTION_CASING_WIDTH,
  LANE_SELECTION_RING,
  LANE_SELECTION_RING_WIDTH,
  CORNER_MARK_SIZE,
  LATE_MARK_COLOR,
  MARK_CASING_COLOR,
  MARK_CASING_WIDTH,
  ORDER_BAR_BORDER_COLOR,
  ORDER_BAR_RAIL_MAX,
  ORDER_BAR_RAIL_MIN,
  ORDER_BAR_RAIL_RATIO,
  ORDER_BAR_SHAPE_MIN_WIDTH,
  SHORTAGE_MARK_COLOR,
  SHORTAGE_MARK_SIZE,
  TIMELINE_GRIDLINE_COLOR,
  TIMELINE_SUBLINE_COLOR,
  TIMELINE_WEEKLINE_COLOR,
  TOOL_MARK_COLOR,
  TOOL_MARK_OUTLINE_COLOR,
  WEEKEND_BG,
  WEEKEND_MIN_BAND_PX
} from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

const ELLIPSIS = "…";

/**
 * 폭에 맞춰 잘라낸 문자열(넘치면 말줄임표). **`fillText`의 maxWidth 인자를 쓰지 않는다** — 그 인자는
 * 글자를 잘라내는 게 아니라 **가로로 눌러서(condense) 폭에 맞추기** 때문에 좁은 바에서 글꼴이
 * 찌그러져 보인다(2026-08-19 사용자 지적). 대신 들어갈 만큼만 잘라 `…`를 붙인다.
 */
const fitText = (ctx, text, maxWidth) => {
  const value = String(text ?? "");
  if (!value || maxWidth <= 0) return "";
  if (ctx.measureText(value).width <= maxWidth) return value;

  const ellipsisWidth = ctx.measureText(ELLIPSIS).width;
  if (ellipsisWidth > maxWidth) return "";

  // 들어가는 최대 접두 길이를 이분탐색으로 찾는다(글자마다 폭이 달라 선형 추정은 틀린다).
  let lo = 0;
  let hi = value.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(value.slice(0, mid)).width + ellipsisWidth <= maxWidth) lo = mid;
    else hi = mid - 1;
  }

  return lo > 0 ? `${value.slice(0, lo)}${ELLIPSIS}` : ELLIPSIS;
};

/** 라벨 좌우 여백 — 좁은 바에서만 줄인다(§7-42). `base` 는 화면별 기본값. */
const labelPad = (width, base) =>
  width < LANE_BAR_TEXT_NARROW_WIDTH ? Math.min(base, LANE_BAR_TEXT_NARROW_PAD) : base;

// 정수 좌표로 그려 안티앨리어싱 번짐을 줄인다.
export const px = (v) => Math.round(v);

// 라운드 사각 경로. 반경은 변 길이의 절반으로 클램프한다(좁은 바에서 경로가 튀는 것을 막는다).
const roundRectPath = (ctx, l, t, r, b, radius) => {
  const rr = Math.max(0, Math.min(radius, (r - l) / 2, (b - t) / 2));
  ctx.beginPath();
  ctx.moveTo(l + rr, t);
  ctx.arcTo(r, t, r, b, rr);
  ctx.arcTo(r, b, l, b, rr);
  ctx.arcTo(l, b, l, t, rr);
  ctx.arcTo(l, t, r, t, rr);
  ctx.closePath();
};

const parseHex = (hex) => {
  const h = String(hex ?? "").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  if (full.length < 6) return null;

  const rgb = [full.slice(0, 2), full.slice(2, 4), full.slice(4, 6)].map(part => parseInt(part, 16));
  return rgb.some(Number.isNaN) ? null : rgb;
};

const luminance = ([r, g, b]) => (0.299 * r + 0.587 * g + 0.114 * b) / 255;

// 채움색 위에서 읽히는 글자색 — 어두운 displayColor에서 검정 글자가 묻히는 것만 보정한다.
export const textColorOn = (hex) => {
  const rgb = parseHex(hex);
  if (!rgb) return "#1a1a1a";
  return luminance(rgb) > 0.6 ? "#1a1a1a" : "#ffffff";
};

/**
 * 바 테두리색 — **채움색을 어둡게(어두운 색은 밝게) 만든 같은 계열**로 그린다(2026-08-19 사용자 결정).
 * 균일한 검정 18%로 그리면 서로 다른 고채도 색이 인접했을 때 경계가 "두 색 사이의 검은 선"으로만
 * 읽혀 색 구분이 흐려진다. 계열색 테두리는 색 정보를 유지하면서 경계를 또렷하게 만든다.
 * hex가 아닌 값(named color 등)이면 공통 테두리색으로 폴백한다.
 */
export const barBorderOn = (hex, fallback = BAR_BORDER_COLOR) => {
  const rgb = parseHex(hex);
  if (!rgb) return fallback;

  // 밝은 색은 0.62배로 어둡게, 어두운 색은 흰쪽으로 40% 당겨서 — 어느 쪽이든 테두리가 보이게.
  const factor = luminance(rgb) > 0.35
    ? rgb.map(v => Math.round(v * 0.62))
    : rgb.map(v => Math.round(v + (255 - v) * 0.4));
  return `rgb(${factor[0]}, ${factor[1]}, ${factor[2]})`;
};

/**
 * **작업 바 전용** 테두리색 — 위 `barBorderOn`의 약한 판이고, **세기가 채움 밝기에 적응한다**(§7-3f H안).
 * 옅은 채움일수록 강하게(0.64) 어둡게 하고 고채도는 약하게(0.86) 둔다 — 옅은 색은 채움이 흰 배경과
 * 1.1~1.3밖에 차이 나지 않아 테두리가 유일한 경계인데, 고채도는 채움만으로 2.5~4.6 대비가 나오므로
 * 강한 선을 두르면 스티커처럼 보인다. **반투명(alpha)은 쓰지 않는다** — 채움 위에 그려지는 링이라
 * 반투명은 어둡기를 절반으로 깎을 뿐이고(실효 0.91) 의도가 값에 드러나지 않는다.
 *
 * ⚠️ `barBorderOn`을 고치지 않고 **함수를 따로 둔 이유**: 그쪽은 주문(요약) 바의 테두리와
 * **하단 레일 채움**에도 쓰인다. 레일이 바뀌면 §7-2f에서 맞춘 두께·무게가 무너지고,
 * 요약 바가 작업 바보다 경계가 강한 것은 위계상 의도된 것이다.
 *
 * 결과는 hex별로 캐시한다 — 자원 간트는 프레임당 바 수백 개를 그리는데 실제 `displayColor` 종류는
 * 20개 안팎이다(해칭 패턴을 캐시하는 것과 같은 이유).
 */
const edgeColorCache = new Map();

export const barEdgeOn = (hex, fallback = BAR_BORDER_COLOR) => {
  const cached = edgeColorCache.get(hex);
  if (cached) return cached;

  // hex가 아닌 값(named color 등)은 캐시하지 않는다 — 폴백이 호출부마다 다르다.
  const rgb = parseHex(hex);
  if (!rgb) return fallback;

  const lum = luminance(rgb);
  const step = BAR_EDGE_STEPS.find(item => lum >= item.minLum);
  // 구간을 못 찾으면(아주 어두운 채움) 어둡게가 아니라 흰쪽으로 밝힌다 — 그래야 테두리가 보인다.
  const c = step
    ? rgb.map(v => Math.round(v * step.darken))
    : rgb.map(v => Math.round(v + (255 - v) * BAR_EDGE_LIGHTEN));

  const color = `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
  edgeColorCache.set(hex, color);
  return color;
};

/**
 * 바 테두리 스트로크 — 두 렌더러(`drawBox`/`drawOrderBox`)가 같은 규칙을 쓰도록 여기 하나로 모았다
 * (복제하면 한쪽만 조정돼 어긋난다).
 *
 * ⚠️ **폭에 따른 세기 조절(페이드)을 없앴다**(2026-09-10, §7-33). `width` 를 더 이상 보지 않는다.
 *
 * 옛 규칙은 폭 3~9px 사이에서 세기를 선형으로 올리고 3px 이하는 아예 안 그렸다. 그 램프는
 * "임계값 하나로 있다/없다를 가르면 폭이 그 값에 몰리는 줌에서 혼재가 보인다"(§7-3g)를 풀려고
 * 넣은 것인데, **폭이 섞여 나열되는 실제 화면에서는 세기가 0.17~0.83 으로 흩어져
 * "테두리가 표시됐다 안 됐다 해서 일관성이 없다"**는 신고가 나왔다 — 불연속을 없앤 대신
 * **연속적인 편차**를 만든 셈이었다.
 *
 * 폭 2px 이하는 사방 1px 링에 채움이 다 먹혀 바 전체가 테두리색이 된다. 그래도 되는 이유:
 * 테두리색은 `barEdgeOn` = **같은 계열의 어두운 색**이라(2026-08-19 결정) 색 정보가 사라지지 않고
 * 어두워질 뿐이고, 옅은 채움은 흰 배경 대비가 1.1 밖에 안 돼 **테두리가 없던 쪽이 오히려 안 보였다.**
 *
 * `ctx.globalAlpha` 는 호출부가 dim(검색 미일치)용으로 설정해 둔 값이라 **건드리지 않는다** —
 * 옛 구현은 여기서 곱했다가 되돌렸다.
 */
const strokeBarEdge = (ctx, { color, fallback, tracePath }) => {
  ctx.strokeStyle = fallback ? barEdgeOn(color, fallback) : barEdgeOn(color);
  ctx.lineWidth = 1;
  tracePath(0.5);
  ctx.stroke();
};

/**
 * 45° 사선 해칭 패턴. 배경 음영이 4종(주말/휴무일/frozen/다운타임)이라 색만으로는 고채도 바와
 * 경쟁해서, 무채색 명도 차 + 패턴으로 구분한다(§7-2a ②). 패턴 객체는 색·간격별로 캐시한다 —
 * 매 프레임 오프스크린 캔버스를 만들면 스크롤이 눌린다.
 */
const hatchCache = new Map();

const hatch = (ctx, color, gap) => {
  const key = `${color}|${gap}`;
  const cached = hatchCache.get(key);
  if (cached) return cached;

  const tile = document.createElement("canvas");
  tile.width = gap;
  tile.height = gap;
  const tctx = tile.getContext("2d");
  tctx.strokeStyle = color;
  tctx.lineWidth = 1;
  // 타일 경계에서 선이 이어지도록 대각선을 세 번 긋는다.
  [-gap, 0, gap].forEach(offset => {
    tctx.beginPath();
    tctx.moveTo(offset, gap);
    tctx.lineTo(offset + gap, 0);
    tctx.stroke();
  });

  const pattern = ctx.createPattern(tile, "repeat");
  hatchCache.set(key, pattern);
  return pattern;
};

/**
 * 주말 음영 + 버킷 세로 격자. ticks는 timelineScales.buildTicks(...).bottom.
 * **날짜 경계선은 항상 그린다**(사용자 제약). 대신 3단 위계로 소음을 줄인다 —
 * 앵커 경계(`tick.major` = 월 1일 등)는 진하게, 일 경계는 옅게, 하위(시간) 격자는 더 옅게.
 * ⚠️ 일 버킷에서 **월요일은 앵커가 아니다** — 앵커로 두면 주말 밴드의 오른쪽 끝만 진해져 밴드가
 * 비대칭으로 보인다(`isMajorBoundary` 주석 참고). 주 리듬은 주말 밴드가 대신 말해준다.
 */
export const drawTimelineGrid = (ctx, { ticks = [], subTicks = [], x0 = 0, top, height, viewLeft, viewRight }) => {
  subTicks.forEach(tick => {
    const left = x0 + tick.left;
    if (left + tick.width < viewLeft || left > viewRight) return;
    ctx.fillStyle = TIMELINE_SUBLINE_COLOR;
    ctx.fillRect(px(left + tick.width) - 1, top, 1, height);
  });

  ticks.forEach(tick => {
    const left = x0 + tick.left;
    if (left + tick.width < viewLeft || left > viewRight) return;

    ctx.fillStyle = tick.major ? TIMELINE_WEEKLINE_COLOR : TIMELINE_GRIDLINE_COLOR;
    ctx.fillRect(px(left) - 1, top, 1, height);       // 경계는 칸의 **시작**에 그린다(major 판정 기준과 일치)
  });
};

/**
 * 주말 밴드 — 전 행 관통. **버킷과 무관하다**(구간은 `timelineScales.buildWeekendRanges`가 날짜로 만든다).
 *
 * 예전에는 `drawTimelineGrid`가 헤더 눈금 칸을 그대로 칠해서 일 버킷에서만 동작했다 — 주 버킷은 칸 1개가
 * 7일이라 "이 칸은 주말"이 성립하지 않았다(§7-6). 지금은 휴무일 밴드와 같은 방식이라 모든 버킷에서 나온다.
 *
 * ⚠️ 이건 "휴무"가 아니라 **요일 리듬** 표시다 — 생산현장은 주말 가동이 흔해서 토·일을 휴무로 칠하면 틀린
 * 정보가 된다. 그래서 불투명도를 2.8%로 두고, **실제 휴무는 자원 비가동(`drawDowntimes`)이 행별로**
 * 그린다(§7-21에서 전역 휴일 레이어를 걷어낸 뒤로는 그것이 유일한 휴무 표시다).
 */
export const drawWeekends = (ctx, { ranges = [], x, top, height }) => {
  ctx.fillStyle = WEEKEND_BG;

  ranges.forEach(range => {
    const x0 = px(x(range.startTs));
    const x1 = px(x(range.endTs));
    // 월 버킷을 크게 축소하면 밴드가 1~2px이 되어 7일마다 줄이 서고 격자와 섞인다 → 그때는 생략한다.
    if (x1 - x0 < WEEKEND_MIN_BAND_PX) return;

    ctx.fillRect(x0, top, x1 - x0, height);
  });
};

// `drawHolidays`(전역 휴일 밴드)는 **제거했다**(2026-08-24, §7-21). 휴일 설정 결과가 자원 비가동으로
// 저장되면서 `drawDowntimes`가 자원 행별로 이미 그린다 — 전 행 관통 레이어는 중복이었고, 자원마다
// 휴무가 다를 수 있어 행별 표시가 더 정확하다. 두 화면 모두 빈 배열만 넘기던 상태였다.

/** Frozen zone(계획 시작~freeze) — 전 행 관통 밴드. 우측 경계는 **점선**으로 다른 음영과 구분한다. */
export const drawFrozenZone = (ctx, { zone, x, top, height }) => {
  if (!zone) return;
  const fx0 = x(zone.startTs);
  const fx1 = x(zone.endTs);
  if (fx1 <= fx0) return;

  ctx.fillStyle = FROZEN_ZONE_COLOR;
  ctx.fillRect(px(fx0), top, px(fx1) - px(fx0), height);

  ctx.save();
  ctx.strokeStyle = FROZEN_ZONE_BORDER_COLOR;
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  ctx.moveTo(px(fx1) - 0.5, top);
  ctx.lineTo(px(fx1) - 0.5, top + height);
  ctx.stroke();
  ctx.restore();
};

export const drawRowDivider = (ctx, { left, width, y }) => {
  ctx.fillStyle = LANE_ROW_DIVIDER_COLOR;
  ctx.fillRect(left, px(y) - 1, width, 1);
};

/** 자원 비가동 구간 — 행 배경(old GANTTGanttBackground). 사선 해칭으로 다른 음영과 구분한다. */
export const drawDowntimes = (ctx, { downtimes = [], x, rowTop, rowHeight }) => {
  downtimes.forEach(dt => {
    const x0 = x(dt.s);
    const x1 = x(dt.e);
    if (x1 <= x0) return;

    const left = px(x0);
    const width = Math.max(1, px(x1) - left);
    const top = px(rowTop);
    const height = rowHeight - 1;

    ctx.fillStyle = DOWNTIME_BG_COLOR;
    ctx.fillRect(left, top, width, height);
    ctx.fillStyle = hatch(ctx, DOWNTIME_HATCH_COLOR, 6);
    ctx.fillRect(left, top, width, height);

    // **구간 양끝 세로 경계선**(§7-20a) — 밴드 알파를 올리는 것보다 시작·끝을 못 박는 편이 가시성에
    // 크게 기여한다. 1px 선은 x.5에 놓아야 선명하다(좌표가 정수라 여기서 +0.5가 픽셀 중앙이 된다).
    ctx.strokeStyle = DOWNTIME_EDGE_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(left + 0.5, top);
    ctx.lineTo(left + 0.5, top + height);
    ctx.moveTo(left + width - 0.5, top);
    ctx.lineTo(left + width - 0.5, top + height);
    ctx.stroke();
  });
};

/**
 * 활동 박스 1개. 결품(shtg)은 마름모, JC는 절반 높이 띠(prev/next 색 구분), 그 외는 테두리+라벨+마크.
 * geom = { left, width, top, height } (이미 계산된 화면 좌표)
 */
/**
 * 바 라벨(작업 주문 코드) 1개. `drawBox`가 기본적으로 직접 부르지만, **분할 다운타임 마크보다 위에**
 * 올려야 할 때는 호출부가 `withLabel:false`로 미루고 마크를 그린 뒤 이 함수를 부른다(§7-20).
 * `rect`는 `drawBox`가 돌려준 정수 사각형을 그대로 넣는다(다시 반올림하면 1px 어긋난다).
 */
export const drawBoxLabel = (ctx, { box, rect, dim = false }) => {
  if (!rect || !box?.text) return;
  if (rect.width < LANE_BAR_TEXT_MIN_WIDTH || rect.height < BAR_LABEL_MIN_HEIGHT) return;

  const baseAlpha = ctx.globalAlpha;
  ctx.globalAlpha = dim ? LANE_DIM_ALPHA : 1;
  ctx.fillStyle = textColorOn(box.color || BAR_DEFAULT_COLOR);
  ctx.font = BAR_FONT;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  // baseline은 **정수로** — 높이가 홀수면 중앙이 .5가 되어 글자가 흐려진다.
  const pad = labelPad(rect.width, 4);
  ctx.fillText(fitText(ctx, box.text, rect.width - pad * 2), rect.left + pad,
    px(rect.top + rect.height / 2));
  ctx.globalAlpha = baseAlpha;
};

export const drawBox = (ctx, { box, geom, dim = false, hovered = false, withLabel = true }) => {
  const { left, top, height } = geom;

  ctx.globalAlpha = dim ? LANE_DIM_ALPHA : 1;

  if (box.type === "shtg") {
    const cx = px(left);
    const cy = px(top + height / 2);
    const half = Math.min(SHORTAGE_MARK_SIZE, height) / 2;
    ctx.fillStyle = SHORTAGE_MARK_COLOR;
    ctx.beginPath();
    ctx.moveTo(cx, cy - half);
    ctx.lineTo(cx + half, cy);
    ctx.lineTo(cx, cy + half);
    ctx.lineTo(cx - half, cy);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
    return null;                 // 부족 마크는 사각형이 아니다(선택 링을 걸 대상이 아님)
  }

  // **인접 바 사이에 틈을 두지 않는다**(2026-08-28 §7-25 — 이전에는 `LANE_BAR_GUTTER`만큼 폭을
  // 깎아 흰 틈을 만들었다). 틈은 오른쪽에서만 깎였으므로 바의 **종료 시각이 실제보다 2px 이르게**
  // 그려졌고, 그 2px이 실제 유휴시간과 구별되지 않았다 — 기본 배율에서 1px ≈ 3.7분이라 "10분 비는
  // 작업"과 "딱 붙은 작업"이 화면에서 같은 틈으로 보였다. 계획 화면에서 **연속인지 아닌지는 판독의
  // 핵심**이라 시각적 구분보다 우선한다.
  //
  // 이제 `x1 == xEnd`라 선행 종료 == 후속 시작이면 두 값이 같은 실수에서 나오므로 `px()`를 통과해도
  // 같은 정수가 되어 **틈도 겹침도 없이 맞물린다.** 경계는 테두리(`strokeBarEdge`)가 전담한다.
  //
  // ⚠️ **좌·우 경계를 정수로 확정하는 것이 이 블록의 핵심이다**(2026-08-21 수정). 이전에는
  //   `barLeft = px(left) + gutter/2` + `width = geom.width - gutter`라서 인접 경계가 세 겹으로 흐려졌다:
  //     ① gutter가 1px이라 barLeft가 **항상 x.5** → 채움 좌우가 반 픽셀씩 번진다
  //     ② 폭을 반올림하지 않아 오른쪽 끝도 소수 좌표 → 같은 번짐
  //     ③ 그 상태에서 테두리를 `+0.5`로 그리면 **정수 좌표**가 된다 — 1px 선은 x.5에 놓여야 선명한데
  //        정수에 놓여 두 픽셀에 50%씩 걸쳤다(`drawOrderBox`가 겪은 것과 같은 부류의 실수, 방향만 반대)
  //   결과가 "붙은 두 작업이 흐린 띠로 이어져 보인다"였다. 좌·우 모두 정수로 확정한다.
  const x0 = px(left);
  const xEnd = px(left + geom.width);
  // ⚠️ 폭 2px 미만은 2px로 **늘려서** 그린다 — 아니면 보이지 않는다. 이 경우에만 바가 실제 시간보다
  // 길어져 다음 바를 살짝 침범할 수 있다(틈이 있던 시절에는 그 틈이 완충 역할을 했다).
  const x1 = Math.max(x0 + LANE_BAR_MIN_WIDTH, xEnd);
  const width = x1 - x0;
  // 세로도 같은 이유로 정수로 확정한다(행 높이가 홀수·소수일 때 위아래 변이 번진다).
  const y0 = px(top);
  const y1 = px(top + height);
  const barHeight = y1 - y0;
  const color = box.color || BAR_DEFAULT_COLOR;

  if (box.type === "jc") {
    // 작업 교체(JC) — **작업 박스와 다른 두께**다: 바 높이의 절반, 세로 중앙, 라운드 없음.
    // old `.BGGanttJCIn > div { height: 50% }` + `align-items: center`(BasicGantt.css:1004-1022).
    // 라벨·테두리·상태 마크는 없다(old도 `<div style='background:…'></div>` 하나만 반환했다).
    //
    // JC는 원래부터 틈 없이 그렸다(old `.BGGanttJCOut`의 `margin:0` + `border-left/right: 0 none`) —
    // 두께가 이미 다르니 섞이지 않고, 자기 작업 박스에 붙어 있는 편이 "이 작업에 딸린 준비시간"으로
    // 읽힌다. §7-25로 작업 바도 같은 규칙이 되면서 `x1 == xEnd`가 됐다.
    // **선택 여부와 무관하게 같은 높이다** — 선택 표현은 링이 전담한다(§7-18a).
    const { top: jcTop, height: jcHeight } = jcBandGeom(y0, barHeight);
    const jcWidth = Math.max(LANE_BAR_MIN_WIDTH, xEnd - x0);

    // prev/next 구분은 화면이 `box.jcKind`로 넘겨준다. 색은 데이터(displayColor)가 아니라
    // 토큰을 쓴다 — 이유는 ganttConstants의 `JC_PREV_COLOR` 주석 참고.
    ctx.fillStyle = box.jcKind === "next" ? JC_NEXT_COLOR : JC_PREV_COLOR;
    ctx.fillRect(x0, jcTop, jcWidth, jcHeight);
    ctx.globalAlpha = 1;
    // 좌표를 돌려준다 — 화면이 **선택된 작업의 JC 띠까지 감싸는 링**을 그리는 데 쓴다(§7-18).
    return { left: x0, top: jcTop, width: jcWidth, height: jcHeight };
  }

  // 라운드 2px — 짧은 바에서 뭉개지지 않는 최대치. 폭이 라운드보다 작으면 사각형으로 떨어진다.
  const radius = Math.min(BAR_RADIUS, width / 2, barHeight / 2);

  // **맞닿은 쪽은 각지게**(§7-28). 라운드는 윗변을 `l+r`~`r-r`까지만 그리므로, 두 바가 시각상
  // 완전히 물려도(`x1 == x0`) 맨 윗줄에 `2×radius`짜리 흰 노치가 남는다 — §7-25로 gutter를 없앤
  // 이득을 코너에서 그대로 까먹고 있었다. 붙은 쪽 코너를 0으로 떨구면 **연속 구간이 하나의 덩어리로
  // 이어지고 바깥 모서리만 둥글게** 남는다.
  //
  // ⚠️ 이 모양이 말하는 것은 "이 작업들이 서로 관련 있다"가 아니라 **"이 자원이 쉬지 않고 돌았다"**다.
  //   개별 작업의 구분은 색·라벨·테두리가 이미 담당한다(코너는 그중 가장 약한 신호였다).
  //   묶여 보이는 인상은 반경에 비례하므로 **반경을 키울 거면 이 점을 다시 검토할 것**(현재 2px에서는
  //   거의 드러나지 않는다).
  //
  // 값은 행 모델의 `markAbutment`가 **시각(ms) 기준으로** 미리 채워 둔다. 값이 없으면(주문 간트처럼
  // 행당 바가 하나인 화면) 둘 다 falsy라 **기존 동작 그대로** 네 코너가 둥글다.
  //
  // ⚠️ **작업 교체(JC) 띠는 이웃이 아니다**(§7-28c) — 절반 높이라 위아래 코너에 닿지 않아 메울 노치가
  //   없다. 그래서 `[A][JC][B]`는 A·B 양 끝이 둥글게 남고, JC 없이 직접 맞닿은 `[A][B]`만 각진다.
  const radiusL = box.abutPrev ? 0 : radius;
  const radiusR = box.abutNext ? 0 : radius;
  // inset 0.5 = 테두리 스트로크용. 위 좌표가 **정수**라서 이 0.5가 실제로 픽셀 중앙을 만든다.
  //
  // ⚠️ **반경도 inset만큼 줄여야 한다**(2026-08-28 §7-26). 좌표만 밀고 반경을 그대로 두면 채움과
  //   테두리의 **코너 중심이 어긋나** 동심이 아니게 된다 — 채움 중심은 `(x0+r, y0+r)`인데 테두리
  //   중심은 `(x0+inset+r, y0+inset+r)`이다. 그러면 코너 대각선에서 채움이 테두리 **바깥으로**
  //   `√2/2 - 0.5 ≈ 0.207px` 삐져나와 밝은 실오라기로 보인다(사용자가 확대해서 지적).
  //   이 값에 `r`이 없다는 점이 중요하다 — **반경을 줄여도 새어남은 그대로다.** 원인은 라운드가
  //   아니라 동심이 아닌 것이고, 직선 구간에서 멀쩡한 것도 거기엔 "중심"이 없어서다.
  //   `radius - inset`으로 주면 중심이 `(x0+r, y0+r)`로 일치하고 테두리 바깥 끝이 `r`이 되어
  //   **기하학적으로 정확히 0**이 된다(근사가 아니다).
  const barPath = (inset = 0) => {
    const l = x0 + inset;
    const t = y0 + inset;
    const r = x1 - inset;
    const b = y1 - inset;
    const cl = Math.max(0, radiusL - inset);
    const cr = Math.max(0, radiusR - inset);
    // 좌·우 반경이 달라 `arcTo` 4연발로는 못 그린다 — 변을 명시적으로 잇고 코너에서만 호를 넣는다.
    // 반경 0이면 `lineTo`로 떨어져 **호 분할 비용조차 없다**(맞닿은 바가 오히려 싸게 그려진다).
    ctx.beginPath();
    ctx.moveTo(l + cl, t);
    ctx.lineTo(r - cr, t); if (cr) ctx.arcTo(r, t, r, t + cr, cr); else ctx.lineTo(r, t);
    ctx.lineTo(r, b - cr); if (cr) ctx.arcTo(r, b, r - cr, b, cr); else ctx.lineTo(r, b);
    ctx.lineTo(l + cl, b); if (cl) ctx.arcTo(l, b, l, b - cl, cl); else ctx.lineTo(l, b);
    ctx.lineTo(l, t + cl); if (cl) ctx.arcTo(l, t, l + cl, t, cl); else ctx.lineTo(l, t);
    ctx.closePath();
  };

  barPath();
  ctx.fillStyle = color;
  ctx.fill();

  // hover 음영 — DOM 화면(gantt.scss의 brightness 필터)과 같은 그림이 되도록 검정 14% 합성.
  if (hovered) {
    ctx.fillStyle = BAR_HOVER_SHADE;
    ctx.fill();
  }

  // 테두리 — **채움색의 어두운(어두우면 밝은) 계열색**. 균일한 검정 테두리는 인접한 두 고채도 색
  // 사이를 검은 선으로 덮어 색 구분을 흐린다(§7-2c 색안 a).
  //
  // **폭 적응형**(§7-3e G안 → §7-3g에서 램프로): 좁은 바에서는 1px 링이 면적의 20%를 먹어 스티커처럼
  // 보인다. 그래서 폭이 좁아질수록 세기를 **연속적으로** 낮추고 슬리버에서 0이 된다 — 임계값 하나로
  // 끊으면 그 값 주변 폭에서 "선이 있는 바와 없는 바"가 섞여 보인다.
  strokeBarEdge(ctx, { color, tracePath: barPath });

  // `withLabel:false`면 라벨을 여기서 그리지 않는다 — 호출부가 **분할 다운타임 마크보다 뒤에**
  // `drawBoxLabel`로 따로 그리기 위한 것이다(§7-20). 자원 간트만 쓰고, 기본값은 종전대로 true라
  // 주문 간트는 영향이 없다.
  // ⚠️ `dim`을 반드시 같이 넘긴다 — `drawBoxLabel`이 `globalAlpha`를 자기 값으로 다시 세팅하므로,
  //    빠뜨리면 위에서 걸어둔 흐리기가 **라벨에만 풀린다**(검색 미일치 바의 글자만 진해진다).
  if (withLabel) drawBoxLabel(ctx, { box, rect: { left: x0, top: y0, width, height: barHeight }, dim });

  // ── 상태 마크 — 좌상단 Tool 사용 / 우상단 납기지연 (§7-11, 시안 prototypes/gantt-bar-marks.html H안+케이싱)
  //
  // **모서리 접힘(fold) + 케이싱**이다. 이전에는 단색 직각삼각만 그려서 "잘 안 띈다"는 지적이 있었고,
  // 원인은 크기가 아니라 **배경색에 따라 대비가 달라지는 것**이었다(displayColor 상당수가 명도 0.9 이상).
  // → 삼각의 사면(바 안쪽을 향한 대각선)에 **흰 케이싱**을 깔면 어떤 채움색 위에서도 마크가 떨어져
  //   나오고, 동시에 "종이 모서리가 접힌" 형태로 읽혀 같은 면적에서 인지가 올라간다.
  //   흰 케이싱은 의존선·선택 링이 이미 쓰는 기법이라 새 규칙이 아니다.
  //
  // 구현: 큰 흰 삼각을 먼저 깔고 그 위에 본 삼각을 얹는다(사면에만 흰 띠가 남는다).
  // **바 경로로 클립**하는 것이 중요하다 — 라운드 2px 모서리 밖으로 삼각이 삐져나가지 않게 한다
  // (클립 없이 그리던 이전 코드는 코너에서 살짝 넘쳤다).
  if (box.hasTool || box.late) {
    // 좁은 바에서 두 삼각이 서로 겹치지 않게 폭을 나눠 갖는다(둘 다 켜진 경우).
    const both = box.hasTool && box.late;
    const budget = both ? Math.floor((width - 1) / 2) : width;
    // **두 마크는 같은 크기다**(`CORNER_MARK_SIZE`) — 좌·우 코너에 크기가 다른 삼각이 앉으면 바가
    // 기울어 보인다. 구분은 위치(좌=Tool / 우=지연)와 색이 한다.
    const size = Math.min(CORNER_MARK_SIZE, budget, barHeight);

    if (size >= 3) {
      ctx.save();
      barPath();
      ctx.clip();

      // 좌·우가 **완전히 같은 도형**이다(크기 동일, 좌우 반전만). `dir`이 그 반전이고 기준 x(`ox`)만
      // 다르다 — 두 곳에 같은 수식을 복제하면 한쪽만 조정돼 어긋난다.
      //
      // **대비를 만드는 방식만 마크별로 갈린다**(§7-22a):
      //   지연 = 빨간 면 + `casing`(사면 띠). 면 자체가 어떤 파스텔과도 대비가 나오므로 사면만으로 충분.
      //   Tool = 흰 면 + `outline`(3변 윤곽). 케이싱은 **빗변에만** 생겨서 위·왼쪽 변이 바 색과
      //          맞닿아 사라진다 — 흰 면에서는 그게 치명적이라 세 변을 모두 긋는다.
      const corner = (ox, dir, { fill, casing, outline }) => {
        if (casing) {
          const outer = size + MARK_CASING_WIDTH;
          ctx.fillStyle = casing;
          ctx.beginPath();
          ctx.moveTo(ox, y0);
          ctx.lineTo(ox + dir * outer, y0);
          ctx.lineTo(ox, y0 + outer);
          ctx.closePath();
          ctx.fill();
        }

        ctx.fillStyle = fill;
        ctx.beginPath();
        ctx.moveTo(ox, y0);
        ctx.lineTo(ox + dir * size, y0);
        ctx.lineTo(ox, y0 + size);
        ctx.closePath();
        ctx.fill();

        if (outline) {
          // 0.5 안쪽으로 밀어 1px 선이 픽셀 중앙에 놓이게 한다(빗변은 대각이라 근사).
          ctx.strokeStyle = outline;
          ctx.lineWidth = 1;
          ctx.lineJoin = "miter";
          ctx.beginPath();
          ctx.moveTo(ox + dir * 0.5, y0 + 0.5);
          ctx.lineTo(ox + dir * (size - 0.5), y0 + 0.5);
          ctx.lineTo(ox + dir * 0.5, y0 + size - 0.5);
          ctx.closePath();
          ctx.stroke();
        }
      };

      // 좌상단 — Tool 사용
      if (box.hasTool) corner(x0, 1, { fill: TOOL_MARK_COLOR, outline: TOOL_MARK_OUTLINE_COLOR });
      // 우상단 — 납기지연
      if (box.late) corner(x1, -1, { fill: LATE_MARK_COLOR, casing: MARK_CASING_COLOR });

      ctx.restore();
    }
  }

  ctx.globalAlpha = 1;

  // 링(선택 표시)이 바와 1px도 어긋나지 않게 **그린 사각형을 그대로 돌려준다**(§7-9).
  return { left: x0, top: y0, width, height: barHeight };
};

/**
 * 주문(Level 0) 박스 — 하위 작업을 요약하는 바.
 *
 * old(`.BGGanttWoBox`)는 밑변 좌·우가 아래로 뾰족한 실루엣이었고 1차 구현도 그 모양을 경로로 그렸는데,
 * 실화면에서 **경계가 흐릿하고 지저분하다**는 지적이 나왔다(2026-08-19). 원인 두 가지:
 *   ① fill과 stroke가 **같은 정수 좌표 경로**여서 1px 선이 픽셀 경계를 반씩 걸쳐 모든 변이 2px 번짐으로
 *      렌더됐다(작업 바 `drawBox`는 0.5 오프셋을 줘서 선명했다 — 이쪽만 빠져 있었다)
 *   ② 노치의 짧은 대각선이 안티앨리어싱으로 뭉개지고, 주문 행이 수십 개 쌓이면 밑변이 들쭉날쭉해 보인다
 * → **라운드 사각형(작업 바와 같은 radius) + 하단 레일**로 바꿨다. 축에 나란한 도형만 쓰므로 항상
 *   선명하고, "하위를 요약한다"는 신호는 레일이 대신한다.
 *
 * label은 호출부가 만든 문자열(품목/수량)이다.
 */
/**
 * 선택 링 — 바 **바깥**에 케이싱(배경색 1.5px) + 링(선택색 2px)을 두른다.
 *
 * CSS `box-shadow: 0 0 0 1.5px bg, 0 0 0 3.5px color`의 캔버스 등가물이다. 채움 영역을 건드리지 않으므로
 * **선택해도 바 크기가 변하지 않는다** — 이전 안쪽 스트로크 방식은 3px 흰 헤일로가 색 면을 먹어 22px 바가
 * 16px로 보였고(짧은 바는 색이 거의 사라졌다), 헤일로와 외곽선을 같은 사각형에 그려 헤일로가 덮였다.
 * 케이싱이 한 겹 띄워 주므로 어두운 계열 바, 1px 간격의 이웃 바와 맞붙어도 링이 뭉치지 않는다.
 *
 * ⚠️ **`rect`는 바가 실제로 그린 사각형이어야 한다** — `geom`(시간축에서 나온 소수 좌표)을 여기서 다시
 * 반올림하면 바와 1px 어긋난다. 실제로 그렇게 만들었다가 오른쪽 여백이 더 넓어 보이는 문제가 났다
 * (2026-08-21): ① `drawBox`는 인접 바 사이 흰 틈을 만들려고 **오른쪽에서 gutter 1px을 깎는다**,
 * ② `px(left) + px(width)`와 `px(left + width)`는 소수에서 1px 달라진다. 그래서 `drawBox`/`drawOrderBox`가
 * 자기가 그린 정수 사각형을 **반환**하고, 화면은 그 값을 그대로 링에 넘긴다(단일 진실 소스).
 *
 * ⚠️ **행 렌더 안에서 부르면 안 된다.** 링이 바깥 3.5px까지 나가는데 자원 간트의 바 간격은 1px이라
 * 다음에 그려지는 이웃 바가 링을 덮는다(세로도 마찬가지 — 다음 행이 덮는다). 반드시 모든 행을 그린 뒤
 * **overlay 패스**에서 호출한다. 이 덮임 현상과 두께 2안(3.5 / 2.5px)은
 * prototypes/gantt-bar-selection.html에서 재현해 비교했다(§7-9).
 */
export const drawSelectionRing = (ctx, { rect }) => {
  if (!rect) return;

  // rect는 이미 정수다(바가 확정한 값) — 다시 반올림하지 않는다.
  const { left, top } = rect;
  const right = left + rect.width;
  const bottom = top + rect.height;

  const ring = (inflate, color, lineWidth) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    roundRectPath(ctx, left - inflate, top - inflate, right + inflate, bottom + inflate, BAR_RADIUS + inflate);
    ctx.stroke();
  };

  // 케이싱은 0~1.5px, 링은 1.5~3.5px 구간을 덮는다(서로 겹치지 않으므로 순서는 무관).
  ring(LANE_SELECTION_CASING_WIDTH / 2, LANE_SELECTION_CASING, LANE_SELECTION_CASING_WIDTH);
  ring(LANE_SELECTION_CASING_WIDTH + LANE_SELECTION_RING_WIDTH / 2, LANE_SELECTION_RING, LANE_SELECTION_RING_WIDTH);
};

export const drawOrderBox = (ctx, { geom, color, label, dim = false, hovered = false }) => {
  const left = px(geom.left);
  const top = px(geom.top);
  // 폭·높이도 **정수로** 확정한다 — `geom`은 시간축 스케일에서 나온 소수라서 그대로 쓰면 오른쪽 변과
  // 하단 레일이 소수 좌표에 놓여 번진다(2026-08-21, `drawBox`와 같은 부류를 여기서도 정리).
  const height = px(geom.top + geom.height) - top;
  const width = Math.max(LANE_BAR_MIN_WIDTH, px(geom.left + geom.width) - left);
  const fill = color || BAR_DEFAULT_COLOR;
  const radius = Math.min(BAR_RADIUS, width / 2, height / 2);

  // inset 0.5 = 테두리 스트로크용(반 픽셀 밀어야 1px 선이 번지지 않는다).
  // 반경도 함께 줄인다 — 이유는 `drawBox`의 같은 함수 주석 참고(§7-26). 주문 바는 요약이라 폭이 넓어
  // 코너 새어남이 덜 눈에 띄었지만, **두 렌더러가 같은 규칙을 쓰지 않으면 한쪽만 조정돼 어긋난다.**
  const barPath = (inset = 0) => {
    const l = left + inset;
    const t = top + inset;
    const r = left + width - inset;
    const b = top + height - inset;
    const rr = Math.max(0, radius - inset);
    ctx.beginPath();
    ctx.moveTo(l + rr, t);
    ctx.arcTo(r, t, r, b, rr);
    ctx.arcTo(r, b, l, b, rr);
    ctx.arcTo(l, b, l, t, rr);
    ctx.arcTo(l, t, r, t, rr);
    ctx.closePath();
  };

  ctx.globalAlpha = dim ? LANE_DIM_ALPHA : 1;

  barPath();
  ctx.fillStyle = fill;
  ctx.fill();

  if (hovered) {
    ctx.fillStyle = BAR_HOVER_SHADE;
    ctx.fill();
  }

  // 하단 레일 — 채움색의 어두운 계열. 라운드 밖으로 새지 않게 바 경로로 클립한다.
  // 두께는 3~6px 사이에서만 움직인다(§7-2f): 얇게 유지해 라벨 밴드를 확보하고, 세로 줌으로 바가
  // 커질 때만 비율로 따라 올려 hairline처럼 보이지 않게 한다.
  const railHeight = width >= ORDER_BAR_SHAPE_MIN_WIDTH
    ? Math.min(ORDER_BAR_RAIL_MAX, Math.max(ORDER_BAR_RAIL_MIN, Math.round(height * ORDER_BAR_RAIL_RATIO)))
    : 0;
  if (railHeight > 0) {
    ctx.save();
    barPath();
    ctx.clip();
    ctx.fillStyle = barBorderOn(fill, ORDER_BAR_BORDER_COLOR);
    ctx.fillRect(left, top + height - railHeight, width, railHeight);
    ctx.restore();
  }

  // 테두리는 선택 여부와 무관하게 같다 — 선택 표현은 바 **바깥**의 링이 맡는다(`drawSelectionRing`).
  // 이전에는 `barPath(selected ? 1 : 0.5)`로 선택 시 경로를 안쪽으로 더 당겨 **바가 실제로 줄어들었다**.
  //
  // **작업 바와 같은 밝기 적응형 테두리를 쓴다**(2026-08-21 사용자 지시, §7-3f 후속). 이전에는
  // `barBorderOn`(0.62 고정)이었는데, 주문 바의 `displayColor`도 옅은 파스텔이 많아 **같은 문제를 반대
  // 방향으로** 갖고 있었다 — 고채도 주문 바에서는 선이 과했다. **폭 세기 램프도 작업 바와 같은
  // 함수(`strokeBarEdge`)를 쓴다**(§7-3g).
  // ⚠️ **하단 레일은 계속 `barBorderOn`(0.62)이다** — 레일은 테두리가 아니라 **채움**이고, 두께·무게를
  // §7-2f에서 따로 맞췄다. 여기까지 옅게 만들면 "하위를 요약한다"는 신호가 사라진다.
  strokeBarEdge(ctx, { color: fill, fallback: ORDER_BAR_BORDER_COLOR, tracePath: barPath });

  // 라벨은 레일을 뺀 위쪽 공간의 중앙. 좁아지면(줌 아웃) 레일을 무시하고 바 전체 중앙에 놓는다
  // — old도 줌 아웃 시 글자를 지우지 않았다.
  const bandHeight = height - railHeight;
  if (label && width >= LANE_BAR_TEXT_MIN_WIDTH) {
    ctx.fillStyle = textColorOn(fill);
    ctx.font = BAR_FONT;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    const cy = px(bandHeight >= BAR_LABEL_MIN_HEIGHT ? top + bandHeight / 2 : top + height / 2);
    const pad = labelPad(width, 5);
    ctx.fillText(fitText(ctx, label, width - pad * 2), left + pad, cy);
  }

  ctx.globalAlpha = 1;
  // drawBox와 같은 이유로 그린 사각형을 반환한다(선택 링의 단일 진실 소스).
  return { left, top, width, height };
};

/**
 * 분할 다운타임 마크 — 활동이 비가동 구간에 걸쳐 쪼개진 자리. 바 위에 얹는다.
 * 검색 중에는 "일치 활동이 감싸고 있는" 것만 테두리로 강조한다(old setDowntimeMark 2x→3x).
 */
/**
 * **JC 띠의 세로 기하** — 작업 바 안에 얇게 가운데 정렬로 앉는다.
 *
 * `drawBox`(띠를 칠할 때)와 `drawDivideMarks`(그 위에 빗금을 얹을 때)가 **같은 값을 써야 한다.**
 * 한때 빗금만 작업 바 기하(`barGeomY`)를 쓰고 있었는데, JC 는 높이가 절반이라 마크가 **띠보다
 * 위에서 시작해 빈 행 배경에 떠 있고** 띠는 거의 다 덮였다 — "휴무에서 jc가 잘 안 보인다"
 * (2026-09-15 신고). 계산을 여기 한 곳으로 모아 두 곳이 갈리지 않게 한다.
 */
export const jcBandGeom = (barTop, barHeight) => {
  const height = Math.max(JC_BAR_MIN_HEIGHT, Math.round(barHeight * JC_BAR_HEIGHT_RATIO));
  return { top: px(barTop + (barHeight - height) / 2), height };
};

export const drawDivideMarks = (ctx, {
  marks = [], boxes = [], x, barTop, barHeight, searchActive = false, matchFn
}) => {
  marks.forEach(dt => {
    const x0 = x(dt.s);
    const x1 = x(dt.e);
    if (x1 <= x0) return;

    // **마크는 자기 박스 위에 얹힌다** — JC 는 작업 바보다 얇고 가운데 정렬이라 기하가 다르다
    // (§7-46. 이걸 안 맞추면 마크가 띠 위쪽 빈 배경에 떠서 띠를 통째로 덮는다).
    const isJc = dt.box?.type === "jc";
    const band = isJc ? jcBandGeom(barTop, barHeight) : { top: px(barTop), height: barHeight };

    // **위쪽 정렬 + 하단 레일**(§7-20a) — 남은 아래쪽에 바 자기 색이 보여야 "이 바가 아래에서
    // 이어진다"(점유하지만 작업 아님)가 읽힌다. 행 높이를 최소로 줄여도 레일이 최소 폭은 남게
    // 클램프하고, 반대로 마크가 3px 밑으로 뭉개지지도 않게 한다.
    //
    // JC 는 비율을 따로 둔다 — 띠가 절반 높이라 같은 0.72 를 쓰면 남는 레일이 3px 뿐이라
    // "이 띠가 이어진다"가 안 읽힌다. 얇은 바일수록 레일 몫을 키워야 한다.
    const ratio = isJc ? DIVIDE_MARK_JC_HEIGHT_RATIO : DIVIDE_MARK_HEIGHT_RATIO;
    const rail = Math.min(
      Math.max(DIVIDE_MARK_MIN_RAIL, band.height - Math.round(band.height * ratio)),
      Math.max(0, band.height - 3)
    );
    const markH = band.height - rail;
    const markTop = band.top;
    const left = px(x0);
    const markW = Math.max(1, px(x1) - left);

    // 바 위에 얹히는 마크라 **행 배경색(거의 투명)으로는 보이지 않는다.** 밝은 면 + 사선으로
    // "여기서 끊겼다"를 표현한다(테마 FP Production, §7-2b). 세기는 `DIVIDE_MARK_FILL` 참고(§7-20).
    ctx.fillStyle = DIVIDE_MARK_FILL;
    ctx.fillRect(left, markTop, markW, markH);
    ctx.fillStyle = hatch(ctx, DOWNTIME_HATCH_COLOR, 6);
    ctx.fillRect(left, markTop, markW, markH);

    // 검색 흐리기 판정 — 마크가 **어느 박스 것인지** 알면 그 박스만 보면 된다.
    // `divideRules.divideMarksOf` 가 `box` 를 실어 준다(보정 간트). 자원 간트는 아직 서버
    // `divideYDowntimes`(박스 참조 없음)를 쓰므로 옛 스캔으로 떨어진다.
    // ⚠️ 옛 스캔의 `box.s < dt.s && dt.e < box.e` 는 **완전히 포함된 마크만** 참이라,
    //    부분 겹침 마크에는 쓸 수 없다 — 그래서 `dt.box` 쪽이 먼저다.
    const emphasized = !searchActive || (dt.box
      ? (!matchFn || matchFn(dt.box.activity))
      : boxes.some(
        box => box.type === "box" && box.s < dt.s && dt.e < box.e && (!matchFn || matchFn(box.activity))
      ));
    if (emphasized) {
      ctx.strokeStyle = DOWNTIME_MARK_BORDER_COLOR;
      ctx.lineWidth = 1;
      ctx.strokeRect(left + 0.5, markTop + 0.5, markW - 1, markH - 1);
    }
  });
};

/**
 * 의존선 1개 — 선행 바 오른쪽 끝 → 후속 바 왼쪽 끝. **항상 목표 왼쪽에서 수평 진입**한다.
 * 라우팅 3케이스 근거는 fp-report-screen-guide.md §E-4.
 *
 * 그리는 방식은 `DEP_LINE_STYLE`(curve / hybrid / elbow) 하나로 갈린다 — 라우팅 좌표 계산은
 * 방식과 무관하게 같다(§7-2h-2). 지금은 "curve"이며, 역방향/간격 0 케이스를 실화면으로 확인하면서
 * 필요하면 "hybrid"로 한 단어만 바꾼다.
 *
 * `emphasis` — 화면 맥락에 따른 세기(§7-2j).
 *   "ambient"(기본) : 행을 펼치면 항상 보이는 배경 정보(주문 간트). 옅게.
 *   "focus"         : 사용자가 더블클릭해 **요청해서 띄운** 경로(자원 간트). 진하고 굵게 + 헤일로 확대.
 */
export const drawDependency = (ctx, { sx, sy, tx, ty, emphasis = "ambient" }) => {
  const focused = emphasis === "focus";
  const lineColor = focused ? DEP_FOCUS_LINE_COLOR : DEP_LINE_COLOR;
  const lineWidth = focused ? DEP_FOCUS_LINE_WIDTH : DEP_LINE_WIDTH;
  const haloWidth = focused ? DEP_FOCUS_HALO_WIDTH : DEP_HALO_WIDTH;

  const forwardGap = tx - sx;
  // 곡선은 정방향에서만 안전하다 — `tx <= sx`면 수평 접선 큐빅이 자기 위로 되접혀 루프가 된다.
  const useCurve = DEP_LINE_STYLE === "curve"
    || (DEP_LINE_STYLE === "hybrid" && forwardGap > DEP_WRAP_GAP_THRESHOLD);

  // ── 곡선: 제어점을 양 끝에서 **수평**으로 둔다(진입 접선이 수평이라 화살촉 방향이 유지된다) ──
  const traceCurve = () => {
    const k = Math.min(Math.max(DEP_CURVE_K_MIN, Math.abs(forwardGap) * 0.5), DEP_CURVE_K_MAX);
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.bezierCurveTo(sx + k, sy, tx - k, ty, tx, ty);
    ctx.stroke();
  };

  // ── 라운드 직각: 직교 통로를 유지하고 꺾이는 지점만 원호로 ──
  // 두 가지를 반드시 지켜야 한다(프로토타입에서 끄고 켜 확인한 함정):
  //  ① 반경 클램프 — `arcTo`는 반경이 인접 선분 길이의 절반을 넘으면 경로가 튄다. 자원 간트는 선분이
  //     2~3px인 구간이 흔해(작업이 딱 붙는 경우) 클램프 없이는 선이 지렁이처럼 튄다. `r < 1`이면 직각.
  //  ② 마지막 코너 제한 — 화살촉 앞 직선 구간을 남긴다. 안 그러면 화살표가 곡선에 얹힌 것처럼 보인다.
  const traceElbow = () => {
    let points;
    if (forwardGap >= 2 * DEP_ELBOW_MARGIN) {
      points = [[sx, sy], [tx - DEP_ELBOW_MARGIN, sy], [tx - DEP_ELBOW_MARGIN, ty], [tx, ty]];
    } else if (forwardGap > DEP_WRAP_GAP_THRESHOLD) {
      const mid = sx + forwardGap / 2;
      points = [[sx, sy], [mid, sy], [mid, ty], [tx, ty]];
    } else {
      const laneY = (sy + ty) / 2;   // 두 행 사이 여백 — 바 위를 되짚지 않는다
      points = [[sx, sy], [sx + DEP_ELBOW_MARGIN, sy], [sx + DEP_ELBOW_MARGIN, laneY],
                [tx - DEP_ENTRY_STUB, laneY], [tx - DEP_ENTRY_STUB, ty], [tx, ty]];
    }

    const lastCornerMax = Math.max(0, DEP_ENTRY_STUB - DEP_ARROW_SIZE - 2);

    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);

    for (let i = 1; i < points.length - 1; i += 1) {
      const [ax, ay] = points[i - 1];
      const [cx, cy] = points[i];
      const [nx, ny] = points[i + 1];
      let radius = Math.min(
        DEP_CORNER_RADIUS,
        Math.hypot(cx - ax, cy - ay) / 2,
        Math.hypot(nx - cx, ny - cy) / 2
      );
      if (i === points.length - 2) radius = Math.min(radius, lastCornerMax);

      if (radius < 1) ctx.lineTo(cx, cy);
      else ctx.arcTo(cx, cy, nx, ny, radius);
    }

    ctx.lineTo(points[points.length - 1][0], points[points.length - 1][1]);
    ctx.stroke();
  };

  const trace = useCurve ? traceCurve : traceElbow;

  // 흰 헤일로 + 본선 2중 스트로크 — 옅은 선이 고채도 바 위를 지나도 읽히게(§7-2a ④).
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.strokeStyle = DEP_HALO_COLOR;
  ctx.lineWidth = haloWidth;
  trace();
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = lineWidth;
  trace();

  const wing = DEP_ARROW_SIZE * 0.6;
  ctx.beginPath();
  ctx.moveTo(tx, ty);
  ctx.lineTo(tx - DEP_ARROW_SIZE, ty - wing);
  ctx.lineTo(tx - DEP_ARROW_SIZE, ty + wing);
  ctx.closePath();
  ctx.fillStyle = lineColor;
  ctx.fill();
};

// ── 보정(편집) 레이어 — AdjustmentGantt 전용 ───────────────────────────────────
// 아래 5개는 **드래그 중에만** 그려진다. 전부 overlay 패스에서 호출한다(행 렌더 안에서 그리면
// 다음 행이 덮는다 — `drawSelectionRing`과 같은 이유).

const DROP_TONES = {
  ok: [DROP_OK_FILL, DROP_OK_EDGE],
  push: [DROP_PUSH_FILL, DROP_PUSH_EDGE],
  deny: [DROP_DENY_FILL, DROP_DENY_EDGE]
};

/**
 * 드롭 판정 표시(§6-5a) — `tone`이 3단계다.
 *
 * `outlineOnly`는 **실이동 방식 전용**이다(§7-15d): 그 자리에 이미 진짜 바가 그려져 있으므로
 * 면을 덮으면 작업 색을 가린다. 판정은 색만 말하면 되니 테두리로 두른다.
 * (면을 칠하는 쪽은 바가 없는 자리를 가리킬 때 쓴다 — 갭 전체가 아니라 **놓였을 때 차지할 구간**만.)
 */
export const drawDropBand = (ctx, { geom, tone = "ok", outlineOnly = false }) => {
  if (!geom) return;

  const [fill, edge] = DROP_TONES[tone] ?? DROP_TONES.ok;
  const left = px(geom.left);
  const top = px(geom.top);
  const right = Math.max(left + LANE_BAR_MIN_WIDTH, px(geom.left + geom.width));
  const bottom = px(geom.top + geom.height);

  // 실이동에서는 바 **바깥**을 두른다 — 바와 같은 사각형에 그리면 테두리가 바 위에 얹혀
  // 채움을 1px씩 먹는다(선택 링을 케이싱까지 두 겹으로 만든 것과 같은 이유, §7-9).
  const inflate = outlineOnly ? 1.5 : 0.5;
  roundRectPath(ctx, left - inflate, top - inflate, right + inflate, bottom + inflate,
    DROP_BAND_RADIUS + (outlineOnly ? 1 : 0));

  if (!outlineOnly) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  ctx.strokeStyle = edge;
  ctx.lineWidth = outlineOnly ? 2 : 1;
  ctx.stroke();
};

/**
 * **놓일 자리 표시**(좌드래그 전용, §7-15h) — 파선 사각형 + 삽입 지점 caret.
 *
 * 이동은 바를 실제로 옮기지 않으므로(원래 자리에 그대로 있다) 이 표시가 "여기 들어간다"를 혼자
 * 말해야 한다. **자리표시는 하나뿐이다** — 옛 고스트 방식이 안 읽혔던 이유는 자리표시가 여럿
 * 겹쳤기 때문이고(§7-15d), 하나면 old처럼 잘 읽힌다.
 * `tone`은 판정 3단계를 그대로 실어 색으로 말한다.
 */
export const drawDropPlaceholder = (ctx, { geom, tone = "ok" }) => {
  if (!geom) return;

  const [fill, edge] = DROP_TONES[tone] ?? DROP_TONES.ok;
  const left = px(geom.left);
  const top = px(geom.top);
  const right = Math.max(left + LANE_BAR_MIN_WIDTH, px(geom.left + geom.width));
  const bottom = px(geom.top + geom.height);

  ctx.save();
  roundRectPath(ctx, left + 0.5, top + 0.5, right - 0.5, bottom - 0.5, DROP_BAND_RADIUS);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.setLineDash(DROP_PLACEHOLDER_DASH);
  ctx.strokeStyle = edge;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.setLineDash([]);

  // 삽입 지점 — 좌측 경계가 곧 놓이는 시작시각이다. 파선만으로는 "폭"이 먼저 읽혀서
  // 어느 시각에 들어가는지가 흐려지므로 그 한 점을 실선으로 못 박는다.
  ctx.strokeStyle = edge;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(left, top - DROP_CARET_SIZE);
  ctx.lineTo(left, bottom);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(left - DROP_CARET_SIZE, top - DROP_CARET_SIZE);
  ctx.lineTo(left + DROP_CARET_SIZE, top - DROP_CARET_SIZE);
  ctx.lineTo(left, top);
  ctx.closePath();
  ctx.fillStyle = edge;
  ctx.fill();
  ctx.restore();
};

/**
 * 잡은 작업의 **출발 자리** — 채우지 않고 점선 윤곽만 남긴다(§7-15d).
 *
 * 실이동 방식에서는 실제 바가 미리보기 위치로 옮겨가므로 "원래 어디였나"가 화면에서 사라진다.
 * 그 하나만 여기서 보전한다 — 얼마나 옮겼는지가 두 위치의 거리로 읽히고, 되돌리려면 어디로
 * 가져가야 하는지도 보인다.
 */
export const drawOriginOutline = (ctx, { geom }) => {
  if (!geom) return;

  const left = px(geom.left);
  const top = px(geom.top);
  const right = Math.max(left + LANE_BAR_MIN_WIDTH, px(geom.left + geom.width));
  const bottom = px(geom.top + geom.height);

  ctx.save();
  ctx.setLineDash(ORIGIN_OUTLINE_DASH);
  ctx.strokeStyle = ORIGIN_OUTLINE_COLOR;
  ctx.lineWidth = 1;
  roundRectPath(ctx, left + 0.5, top + 0.5, right - 0.5, bottom - 0.5, BAR_RADIUS - 0.5);
  ctx.stroke();
  ctx.restore();
};

/**
 * **밀려난** 작업 표식 — 좌측 얇은 레일. 옛 밀림 고스트가 "다른 위치"로 말하던 것을 **색**으로 옮긴
 * 것이다(실이동에서는 바가 이미 밀린 자리에 있으므로 위치로는 구분이 안 된다).
 * 바 위에 덮는 면이 아니라 왼쪽 변에만 얹어 채움색을 가리지 않는다.
 */
export const drawPushedRail = (ctx, { rect }) => {
  if (!rect || rect.width < 2) return;

  ctx.fillStyle = PUSHED_RAIL_COLOR;
  ctx.fillRect(rect.left, rect.top, Math.min(PUSHED_RAIL_WIDTH, rect.width), rect.height);
};

/** old `OnGanttDragTip` — 커서 옆 `시작 ~ 종료` 라벨. 화면 오른쪽 끝에서 잘리지 않게 접는다. */
export const drawDragTip = (ctx, { x, y, text, viewWidth }) => {
  const value = String(text ?? "");
  if (!value) return;

  ctx.font = DRAG_TIP_FONT;
  const width = ctx.measureText(value).width + DRAG_TIP_PAD_X * 2;
  const height = 16 + DRAG_TIP_PAD_Y * 2 - 6;

  let left = px(x + 12);
  if (viewWidth != null && left + width > viewWidth - 4) left = px(x - 12 - width);
  const top = px(y + DRAG_TIP_OFFSET_Y);

  roundRectPath(ctx, left, top, left + width, top + height, 3);
  ctx.fillStyle = DRAG_TIP_BG;
  ctx.fill();

  ctx.fillStyle = DRAG_TIP_COLOR;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillText(value, left + DRAG_TIP_PAD_X, top + height / 2);
};

/** 변경된 작업 표식 — 바 **좌상단** 삼각(납기지연 마크는 우상단이라 겹치지 않는다). */
/**
 * **떨어진 JC 와 주인 작업을 파선으로 잇는다**(§7-40 A안).
 *
 * `geom` 은 **틈**의 사각형이다(JC 끝 ~ 작업 시작, 또는 그 거울상) — 바가 아니다.
 * 세로 중앙에 파선을 긋고 양 끝에 짧은 세로 눈금을 찍는다.
 *
 * 붙어 있으면 호출부가 아예 부르지 않는다. 폭이 `JC_LINK_MIN_WIDTH` 미만이면 파선이 점
 * 하나로 뭉개져 오히려 노이즈라 그리지 않는다.
 */
export const drawJcLink = (ctx, { geom }) => {
  if (!geom || geom.width < JC_LINK_MIN_WIDTH) return;

  const y = Math.round(geom.top + geom.height / 2) + 0.5;   // 0.5 보정 — 1px 선이 번지지 않게
  const left = geom.left;
  const right = geom.left + geom.width;
  const cap = Math.min(JC_LINK_CAP_HEIGHT, geom.height) / 2;

  ctx.save();
  ctx.strokeStyle = JC_LINK_COLOR;
  ctx.lineWidth = 1;

  ctx.setLineDash(JC_LINK_DASH);
  ctx.beginPath();
  ctx.moveTo(left, y);
  ctx.lineTo(right, y);
  ctx.stroke();

  // 눈금은 실선이다 — 파선으로 찍으면 짧아서 사라진다.
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(Math.round(left) + 0.5, y - cap);
  ctx.lineTo(Math.round(left) + 0.5, y + cap);
  ctx.moveTo(Math.round(right) - 0.5, y - cap);
  ctx.lineTo(Math.round(right) - 0.5, y + cap);
  ctx.stroke();
  ctx.restore();
};

export const drawChangedMark = (ctx, { rect }) => {
  if (!rect || rect.width < 6) return;

  const size = Math.min(CHANGED_MARK_SIZE, rect.width, rect.height);
  ctx.beginPath();
  ctx.moveTo(rect.left, rect.top);
  ctx.lineTo(rect.left + size, rect.top);
  ctx.lineTo(rect.left, rect.top + size);
  ctx.closePath();
  ctx.fillStyle = CHANGED_MARK_COLOR;
  ctx.fill();
};
